import React, { Suspense } from 'react';
import { Card } from '@startuptools/ui/Card';
import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Breadcrumbs } from '@startuptools/ui/Breadcrumbs';
import { Trans, useLingui } from '@lingui/react';
import { GqlDocSystemTagKind } from '../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { DocKind } from '../../../free-templates/types';
import { Folder } from './folder';
import { useFileViewSuspenseQuery } from './file-view.graphql';
import { Agreement } from '../templates/agreement';
import { usePaginatedQuery } from '@startuptools/ui/custom/UsePaginatedQuery';
import { useNavigate } from '../../../components/Navigate';
import { Link, useParams } from 'react-router-dom';
import { GqlCompany } from '../../../graphql/base-types.graphql';

type Props = {
  showNewDocumentDialog: (
    kind: DocKind,
    company: Pick<GqlCompany, 'id' | 'name' | 'organizationNumber' | 'addressAsStr'>,
  ) => Promise<boolean>;
};

const Inner = ({ showNewDocumentDialog }: Props) => {
  const { fileId } = useParams();
  const [searchString, setSearchString] = React.useState('');
  const companyId = useCompanyId();
  const { link } = useNavigate();
  const { i18n } = useLingui();

  const { data, refetch, page, rowsPerPage, setPage, setRowsPerPage } = usePaginatedQuery(useFileViewSuspenseQuery, {
    variables: {
      companyId,
      fileId,
      filter: { searchString },
      lang: i18n.locale,
    },
    fetchPolicy: 'cache-and-network',
  });

  return (
    <Stack p={2} borderRadius={1} borderColor="neutral.outlinedBorder" bgcolor="white" minHeight={100} gap={2}>
      <Card variant="solid" sx={{ backgroundColor: 'white', padding: 0 }}>
        <Stack spacing={2}>
          <Typography level="h2">
            <Trans id="Documents & e-signing" />
          </Typography>

          <Box>
            <Breadcrumbs size="sm" separator={<ChevronRightRoundedIcon />}>
              <Stack
                component={Link}
                to={link('/documents/company-documents')}
                direction="row"
                gap={1}
                alignItems="center"
                sx={{ cursor: 'pointer' }}
              >
                <HomeRoundedIcon />
                <Typography fontWeight={600}>
                  <Trans id="Your files" />
                </Typography>
              </Stack>
              {data.file.parent && (
                <Typography
                  component={Link}
                  to={link(`/documents/company-documents/${data.file.parent!.id}`)}
                  fontWeight={600}
                  sx={{ cursor: 'pointer' }}
                >
                  {data.file.parent.filenameSecondaryLang && i18n.locale === 'en'
                    ? data.file.parent.filenameSecondaryLang
                    : data.file.parent.filename}
                </Typography>
              )}
              {fileId !== null && <Typography fontWeight={600}>{data.file.filename}</Typography>}
            </Breadcrumbs>
          </Box>

          {(!fileId || data.file.kind === GqlDocSystemTagKind.Folder) && (
            <Folder
              pagination={{
                totalCount: data.file.children?.totalCount ?? 0,
                page,
                rowsPerPage,
                setPage,
                setRowsPerPage,
              }}
              folderId={fileId!}
              searchString={searchString}
              showNewDocumentDialog={kind => showNewDocumentDialog(kind, data.company)}
              data={data}
              refetch={refetch}
              onSearch={setSearchString}
            />
          )}
          {data.file.agreementId && data.file.kind === GqlDocSystemTagKind.Agreement && (
            <Agreement agreementId={data.file.agreementId} />
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export const File = (props: Props) => {
  return (
    <Suspense
      fallback={
        <Stack border={0} minHeight={100} gap={2}>
          <Typography level="h2">
            <Trans id="Documents & E-signing" />
          </Typography>

          <Card variant="solid" sx={{ backgroundColor: 'white', padding: 0 }}></Card>
        </Stack>
      }
    >
      <Inner {...props} />
    </Suspense>
  );
};
